/**
 * CDM Adapters — registry view + per-adapter health + Run-now buttons.
 *
 * Per ADR-0007 the existing Connector Health page tracks vendor health
 * KPIs; this page is the entity-ingest counterpart, showing every CDM
 * adapter's last-run / record-count / archetypes + per-source events
 * count from the cdm_events firehose.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Activity, ChevronLeft, RefreshCw, Play, CheckCircle2, AlertTriangle,
  XCircle, Clock,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface AdapterDef {
  name: string
  display_name: string
  archetypes: string[]
}

interface Health {
  source: string
  status: string
  observed_at: string | null
  freshness_lag: number | null
  error_rate: number | null
  record_count: number | null
}

const STATUS_TONE: Record<string, string> = {
  ok:       'bg-emerald/15 text-emerald border-emerald/30',
  degraded: 'bg-amber/15 text-amber-light border-amber/30',
  failing:  'bg-rose/15 text-rose-light border-rose/30',
  unknown:  'bg-text-muted/15 text-text-muted border-border',
}

export default function CdmAdaptersPage() {
  const [adapters, setAdapters] = useState<AdapterDef[]>([])
  const [health, setHealth] = useState<Health[]>([])
  const [loading, setLoading] = useState(false)
  const [running, setRunning] = useState<string | null>(null)
  const [message, setMessage] = useState('')

  const load = () => {
    setLoading(true)
    Promise.all([
      api.get('/api/cdm/adapters'),
      api.get('/api/cdm/adapters/health'),
    ])
      .then(([a, h]) => {
        setAdapters(a.data?.adapters || [])
        setHealth(h.data?.sources || [])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const healthBySource = useMemo(() => {
    const m: Record<string, Health> = {}
    for (const h of health) m[h.source] = h
    return m
  }, [health])

  const runOne = async (name: string) => {
    setRunning(name)
    setMessage(`Running ${name}…`)
    try {
      const { data } = await api.post(`/api/cdm/adapters/${name}/run`)
      const stats = data?.stats || {}
      setMessage(`${name}: ${stats.events_emitted ?? 0} events, ${stats.matched ?? 0} matched, ${stats.new_goldens ?? 0} new, ${stats.conflicts_opened ?? 0} conflicts (${data.elapsed_ms}ms)${data.error ? ' — ' + data.error : ''}`)
      load()
    } catch (e: any) {
      setMessage(`${name}: ${e.response?.data?.detail || 'failed'}`)
    } finally {
      setRunning(null)
    }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Activity size={18} className="text-blue-light" /> CDM ADAPTERS
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          {adapters.length} registered · {health.filter(h => h.status === 'ok').length} healthy
        </span>
        <button type="button" onClick={load} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {adapters.map(a => {
          const h = healthBySource[a.name]
          const status = h?.status || 'unknown'
          const StatusIcon = status === 'ok' ? CheckCircle2 : status === 'failing' ? XCircle : status === 'degraded' ? AlertTriangle : Clock
          return (
            <div key={a.name} className="card p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-text-primary">{a.display_name}</div>
                  <code className="text-[10px] font-mono text-text-muted">{a.name}</code>
                </div>
                <span className={clsx('badge text-[9px] inline-flex items-center gap-1 border', STATUS_TONE[status])}>
                  <StatusIcon size={10} /> {status}
                </span>
              </div>

              <div className="flex flex-wrap gap-1">
                {a.archetypes.map(arch => (
                  <span key={arch} className="badge text-[9px] bg-surface-raised text-text-secondary border border-border">{arch}</span>
                ))}
              </div>

              {h && (
                <div className="text-[10px] text-text-muted space-y-0.5">
                  <div>Last seen: {h.observed_at ? new Date(h.observed_at).toLocaleString() : 'never'}</div>
                  {h.record_count !== null && <div>Last run records: <span className="font-mono text-text-secondary">{h.record_count.toLocaleString()}</span></div>}
                  {h.error_rate !== null && h.error_rate > 0 && <div className="text-rose-light">Error rate: {(h.error_rate * 100).toFixed(0)}%</div>}
                </div>
              )}

              <button type="button" onClick={() => runOne(a.name)} disabled={running !== null}
                className="btn-ghost w-full justify-center"
                title={`Run ${a.display_name} pull (admin only)`}>
                <Play size={11} /> {running === a.name ? 'Running…' : 'Run now'}
              </button>
            </div>
          )
        })}
      </div>

      <div className="text-[10px] text-text-muted text-center">
        Adapter pulls are also scheduled via Celery beat. See ingest.py for cron times.
      </div>
    </div>
  )
}
